"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import type { Locale } from "@/i18n-config";
import type { HelpCategory, HelpFaqItem } from "@/services/help.server";
import type { HelpCenterPageCopy } from "@/content/help-center-copy";
import { cn } from "@/utils/cn";

type HelpCenterShellProps = {
  lang: Locale;
  categories: HelpCategory[];
  faqs: HelpFaqItem[];
  copy: HelpCenterPageCopy;
};

type GroupedFaqs = {
  categorySlug: string;
  categoryTitle: string;
  items: HelpFaqItem[];
};

function normalizeSearchTerm(value: string): string {
  return value.trim().toLowerCase();
}

function decodeHash(rawHash: string): string {
  if (!rawHash.startsWith("#")) return "";
  const value = rawHash.slice(1).trim();
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function encodeHash(value: string): string {
  return encodeURIComponent(value);
}

function updateUrlHash(nextSlug: string | null) {
  if (typeof window === "undefined") return;

  const { pathname, search } = window.location;
  if (!nextSlug) {
    window.history.replaceState(null, "", `${pathname}${search}`);
    return;
  }

  window.history.replaceState(null, "", `${pathname}${search}#${encodeHash(nextSlug)}`);
}

function scrollToFaq(slug: string) {
  if (typeof window === "undefined") return;

  const node = document.getElementById(`faq-${slug}`);
  if (!node) return;
  node.scrollIntoView({ block: "start" });
}

export function HelpCenterShell(props: HelpCenterShellProps) {
  const { lang, categories, faqs, copy } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string>("all");
  const [openFaqSlug, setOpenFaqSlug] = useState<string | null>(null);

  const faqsBySlug = useMemo(() => {
    const map = new Map<string, HelpFaqItem>();
    for (const faq of faqs) map.set(faq.slug, faq);
    return map;
  }, [faqs]);

  useEffect(() => {
    const applyHash = () => {
      const hashedSlug = decodeHash(window.location.hash);
      if (!hashedSlug) return;

      const matched = faqsBySlug.get(hashedSlug);
      if (!matched) return;

      setOpenFaqSlug(matched.slug);
      setSelectedCategorySlug(matched.categorySlug);
      requestAnimationFrame(() => scrollToFaq(matched.slug));
    };

    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => {
      window.removeEventListener("hashchange", applyHash);
    };
  }, [faqsBySlug]);

  const filteredFaqs = useMemo(() => {
    const normalizedSearch = normalizeSearchTerm(searchTerm);

    return faqs.filter((faq) => {
      const matchesCategory = selectedCategorySlug === "all" || faq.categorySlug === selectedCategorySlug;
      if (!matchesCategory) return false;
      if (!normalizedSearch) return true;
      return faq.searchText.includes(normalizedSearch);
    });
  }, [faqs, searchTerm, selectedCategorySlug]);

  useEffect(() => {
    if (!openFaqSlug) return;
    if (!filteredFaqs.some((faq) => faq.slug === openFaqSlug)) {
      setOpenFaqSlug(null);
    }
  }, [filteredFaqs, openFaqSlug]);

  const groupedFaqs = useMemo((): GroupedFaqs[] => {
    const grouped = new Map<string, GroupedFaqs>();
    const categoryLookup = new Map(categories.map((category) => [category.slug, category]));

    for (const faq of filteredFaqs) {
      if (!grouped.has(faq.categorySlug)) {
        const fallbackCategory = categoryLookup.get(faq.categorySlug);
        grouped.set(faq.categorySlug, {
          categorySlug: faq.categorySlug,
          categoryTitle: fallbackCategory?.title ?? faq.categoryTitle,
          items: [],
        });
      }

      grouped.get(faq.categorySlug)?.items.push(faq);
    }

    return Array.from(grouped.values()).sort((a, b) => {
      const aCategory = categoryLookup.get(a.categorySlug);
      const bCategory = categoryLookup.get(b.categorySlug);
      const orderDelta = (aCategory?.order ?? 9999) - (bCategory?.order ?? 9999);
      if (orderDelta !== 0) return orderDelta;
      return a.categoryTitle.localeCompare(b.categoryTitle, "en", { sensitivity: "base" });
    });
  }, [categories, filteredFaqs]);

  const hasFaqData = faqs.length > 0;
  const hasSearchOrCategoryFilter = normalizeSearchTerm(searchTerm).length > 0 || selectedCategorySlug !== "all";
  const hasFilteredFaqs = filteredFaqs.length > 0;

  const handleFaqOpen = (faq: HelpFaqItem) => {
    setOpenFaqSlug(faq.slug);
    setSelectedCategorySlug(faq.categorySlug);
    updateUrlHash(faq.slug);
  };

  return (
    <div>
      <div className="mt-6">
        <label htmlFor="help-search" className="text-sm font-medium text-[#2d3f3a]">
          {copy.searchLabel}
        </label>
        <input
          id="help-search"
          type="search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={copy.searchPlaceholder}
          className="mt-2 min-h-11 w-full rounded-xl border border-[#d7cab1] bg-[#fffaf1] px-3.5 text-sm text-[#243632] outline-none transition-colors focus:border-[#0f766e]"
          autoComplete="off"
        />
      </div>

      <div className="mt-5 -mx-4 overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
        <div className="flex w-max gap-2 md:w-auto md:flex-wrap">
          <button
            type="button"
            onClick={() => setSelectedCategorySlug("all")}
            className={cn(
              "min-h-10 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors",
              selectedCategorySlug === "all"
                ? "border-[#0f766e]/30 bg-[#edf9f6] text-[#0f766e]"
                : "border-[#d7cab1] bg-[#fffaf1] text-[#32453f] hover:bg-[#fbf6eb]"
            )}
          >
            {copy.categoryAllLabel}
          </button>
          {categories.map((category) => (
            <button
              key={category.slug}
              type="button"
              onClick={() => setSelectedCategorySlug(category.slug)}
              className={cn(
                "min-h-10 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors",
                selectedCategorySlug === category.slug
                  ? "border-[#0f766e]/30 bg-[#edf9f6] text-[#0f766e]"
                  : "border-[#d7cab1] bg-[#fffaf1] text-[#32453f] hover:bg-[#fbf6eb]"
              )}
            >
              {category.title}
            </button>
          ))}
        </div>
      </div>

      {!hasFaqData ? (
        <div className="mt-8 rounded-2xl border border-[#d8cab2] bg-[#fffaf1] px-5 py-4 text-sm text-[#455851]">{copy.emptyFaqState}</div>
      ) : null}

      {hasFaqData && hasSearchOrCategoryFilter && !hasFilteredFaqs ? (
        <div className="mt-8 rounded-2xl border border-[#d8cab2] bg-[#fffaf1] px-5 py-4 text-sm text-[#455851]">{copy.noSearchResults}</div>
      ) : null}

      {groupedFaqs.map((group) => (
        <section key={group.categorySlug} className="mt-10">
          <h2 className="text-xl font-semibold text-[#1f2a27] md:text-2xl">
            {group.categoryTitle}
            <span className="sr-only">
              {copy.categoryHeadingPrefix} {group.categoryTitle}
            </span>
          </h2>

          <div className="mt-4 space-y-3">
            {group.items.map((faq) => {
              const isOpen = openFaqSlug === faq.slug;
              const triggerId = `faq-trigger-${faq.slug}`;
              const panelId = `faq-panel-${faq.slug}`;

              return (
                <article
                  key={faq.id}
                  id={`faq-${faq.slug}`}
                  className="rounded-2xl border border-[#d8cab2] bg-[#fffaf1] p-4 md:p-5"
                >
                  <button
                    id={triggerId}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => handleFaqOpen(faq)}
                    className="w-full text-left text-base leading-snug font-semibold text-[#1f2a27] outline-none transition-colors focus:text-[#0f766e]"
                  >
                    <span className="block">
                      {faq.question}
                    </span>
                  </button>

                  <div
                    className={cn(
                      "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                      isOpen ? "mt-3 grid-rows-[1fr] opacity-100" : "mt-0 grid-rows-[0fr] opacity-0"
                    )}
                  >
                    <div className="overflow-hidden">
                      <div
                        id={panelId}
                        role="region"
                        aria-labelledby={triggerId}
                        className="zv-richtext text-sm leading-relaxed text-[#3d4e48]"
                      >
                        <PortableText
                          value={faq.answerBlocks}
                          components={{
                            block: {
                              normal: ({ children }) => <p className="leading-relaxed">{children}</p>,
                              h2: ({ children }) => <h2 className="text-2xl leading-tight text-[#1f2a27]">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-xl leading-tight text-[#1f2a27]">{children}</h3>,
                              h4: ({ children }) => <h4 className="text-lg leading-tight text-[#1f2a27]">{children}</h4>,
                            },
                            list: {
                              bullet: ({ children }) => (
                                <ul className="mb-3 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
                              ),
                              number: ({ children }) => (
                                <ol className="mb-3 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
                              ),
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}

      <section className="mt-12 rounded-2xl border border-[#d8cab2] bg-[#fffaf1] px-5 py-6 md:px-6">
        <h2 className="text-2xl font-semibold text-[#1f2a27]">{copy.contactHeading}</h2>
        <p className="mt-2 text-sm text-[#43554f]">{copy.contactBody}</p>
        <div className="mt-5">
          <Link
            href={`/${lang}/help/contact`}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-[#0f766e]/30 bg-[#0f766e] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#0d6860]"
          >
            {copy.contactButton}
          </Link>
        </div>
      </section>
    </div>
  );
}
